
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import pluralize from 'pluralize';
import { db } from '@/firebase';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { titleCase } from '@/filters';
import {
	ASPECT_RATIO_FILTER_OPTIONS,
	RESOLUTION_FILTER_OPTIONS,
	FAVORITE_FILTER_OPTIONS,
	AppliedLibraryFilters,
	FacetOption,
	LIBRARY_FILTER_FIELDS,
	LibraryFilterDraft,
	LibraryScope,
	buildAppliedLibraryFilters,
	buildDraftFromApplied,
	clearAndApplyLibraryFilters,
	countActiveLibraryFilters,
	createEmptyLibraryFilterDraft,
	groupFilterTagsByType,
	normalizeLibraryFilterValue,
	reconcileTeamSelection,
} from '@/utils/libraryFilterFacets';
import { loadBoardsForScope } from '@/utils/libraryBoards';
import FilterOptionAvatar from '@/components/ImageGallery/FilterOptionAvatar.vue';
import DatePicker from '@/components/base/DatePicker.vue';

// Self-contained library filter panel: owns the draft fields + the committed
// (applied) snapshot, and emits `apply` with the AppliedLibraryFilters object
// whenever the committed filters change. The host renders its own "Filter"
// toggle button and passes `expanded` to show/hide this panel; the host then
// feeds the emitted applied filters into <image-gallery>. Shared by the Library
// page and the Image Gallery dialog so both read/behave identically.
export default Vue.extend({
	name: 'LibraryFilterPanel',
	components: {
		DatePicker,
		FilterOptionAvatar,
	},
	props: {
		// Host-controlled visibility (toggled by the host's Filter button).
		expanded: { type: Boolean, default: false },
		// Optional one-time seed for the committed filters (e.g. the Library
		// page restoring filters from the URL). Read once on created.
		initialApplied: { type: Object as () => AppliedLibraryFilters | null, default: null },
		// Drop the panel's own surface (background/shadow/border/rail) so it
		// blends into the host's background — used in the gallery dialog, whose
		// body grey differs from the Library page's.
		flat: { type: Boolean, default: false },
		// Show the scope selector (Mine / Team) + owner filter. The
		// host enables this where team/cross-team reads make sense and supplies
		// the option lists below.
		showScope: { type: Boolean, default: false },
		// Owner (filter-by-user) options for the active scope: { value: userId, text }.
		memberOptions: { type: Array as () => FacetOption[], default: () => [] },
		// Teams the caller manages, for cross-team scope: { value: teamId, text }.
		teamOptions: { type: Array as () => FacetOption[], default: () => [] },
	},
	data () {
		return {
			ASPECT_RATIO_FILTER_OPTIONS,
			RESOLUTION_FILTER_OPTIONS,
			FAVORITE_FILTER_OPTIONS,
			// Draft state — bound to the controls; not live until Apply. One field per
			// LIBRARY_FILTER_FIELDS entry (aspectRatioFilter, ..., scopeFilter,
			// teamIdsFilter), so a new filter needs no edit here.
			...createEmptyLibraryFilterDraft(),
			// "User email" filter options: team members, loaded per team (cached),
			// unioned across the selected teams, and searched client-side. Capped
			// per team — very large teams surface their first members alphabetically.
			MEMBER_LOAD_CAP: 100,
			memberCacheByTeam: {} as Record<string, FacetOption[]>,
			memberOptionsLoaded: [] as FacetOption[],
			loadingMembers: false,
			// "Board" filter options: the boards readable in the active scope,
			// cached per scope-key and searched client-side (see libraryBoards).
			boardCacheByKey: {} as Record<string, FacetOption[]>,
			boardOptionsLoaded: [] as FacetOption[],
			loadingBoards: false,
		};
	},
	created () {
		if (this.initialApplied) {
			this.hydrate(this.initialApplied);
		}
	},
	computed: {
		...mapState([
			'user',
			'team',
			'publicRunnitNodeDefTagsMap',
			'publicRunnitNodeDefsMap',
			'companyRunnitNodeDefsMap',
		]),
		...mapGetters([
			'displayLabels',
		]),
		groupedFilterTags () {
			return groupFilterTagsByType(this.publicRunnitNodeDefTagsMap || {});
		},
		// Tool dropdown spans public + company tools, deduped by id. The node defs
		// in these maps are Vuefire-bound, so `avatar` is already the resolved
		// Avatar object — we pass it straight through for the picker to render.
		toolFilterOptions (): FacetOption[] {
			const byId: Record<string, FacetOption> = {};
			const collect = (map: Record<string, any>) => {
				Object.values(map || {}).forEach((d: any) => {
					if (d && d.id && d.title && !byId[d.id]) {
						byId[d.id] = { value: d.id, text: d.title, avatar: d.avatar || null };
					}
				});
			};
			collect(this.publicRunnitNodeDefsMap);
			collect(this.companyRunnitNodeDefsMap);
			return Object.values(byId).sort((a, b) => a.text.localeCompare(b.text));
		},
		// Count of active filters — drives the header chip. Counts only narrowing
		// fields, so scope/teamIds (always truthy) don't inflate a pristine panel to 2.
		activeFilterCount (): number {
			return countActiveLibraryFilters(this.currentDraft);
		},
		// Two scopes: whose work you are looking at. "Team" then asks WHICH teams,
		// via the picker below. One team and several teams are the same question
		// with a different answer, so there is no separate cross-team mode to
		// choose before you can choose a team.
		scopeOptions (): { value: LibraryScope; text: string; icon: string }[] {
			return [
				{ value: 'personal', text: 'Mine', icon: 'mdi-account' },
				{ value: 'team', text: titleCase(this.displayLabels.team), icon: 'mdi-account-group' },
			];
		},
		// The teams whose members populate the "User email" picker. An empty
		// Team selection stays empty; it must never fall back to an unavailable
		// active Team.
		relevantTeamIds (): string[] {
			if (this.scopeFilter === 'personal') return [];
			const picked: string[] = Array.isArray(this.teamIdsFilter) ? this.teamIdsFilter : [];
			return picked;
		},
		// Stable key so the members watcher only reloads when the set changes.
		relevantTeamIdsKey (): string {
			return this.relevantTeamIds.slice().sort().join(',');
		},
		// Loaded members win; fall back to a host-supplied prop (e.g. the dialog).
		memberOptionsForPicker (): FacetOption[] {
			return this.memberOptionsLoaded.length ? this.memberOptionsLoaded : this.memberOptions;
		},
		// The boards to offer in the picker (loaded for the active scope).
		boardOptionsForPicker (): FacetOption[] {
			return this.boardOptionsLoaded;
		},
		// The teams whose accessible boards the picker lists: every team you
		// belong to on Mine (so it spans all boards you can access), the active
		// picked teams, or the active one before the picker is touched.
		boardScanTeamIds (): string[] {
			if (this.scopeFilter === 'personal') {
				return Object.keys((this.user && this.user.teamIds) || {});
			}
			return this.relevantTeamIds;
		},
		// Stable key for the board list — the caller, whether personal boards are
		// included, and the teams scanned. The watcher reloads only on a change.
		boardLoadKey (): string {
			const userId: string = (this.user && this.user.id) || '';
			const teams: string = this.boardScanTeamIds.slice().sort().join(',');
			const kind: string = this.scopeFilter === 'personal' ? 'personal' : 'team';
			return `${kind}:${userId}:${teams}`;
		},
		// The draft fields packaged for the helper checks/emit.
		currentDraft (): LibraryFilterDraft {
			const draft = {} as LibraryFilterDraft;
			LIBRARY_FILTER_FIELDS.forEach((f) => {
				draft[f.draftKey] = this[f.draftKey];
			});
			return draft;
		},
		// Whether the draft form has anything to clear — gates "Clear all". Counted
		// over the panel's own controls, so a search term alone shows no Clear all.
		hasDraftFilters (): boolean {
			return this.activeFilterCount > 0;
		},
		// Team scope with nothing chosen. Marked on the FIELD rather than on the
		// disabled Apply button, because that is where the caller has to act, and
		// a disabled button emits no pointer events to explain itself with.
		//
		// Shown as soon as Team scope is entered empty, which reads as a
		// requirement rather than a scolding here: selectScope seeds the picker
		// whenever the active team is eligible, so the only callers who ever see
		// this are the ones who MUST choose explicitly and have no other way to
		// learn that. It clears the moment a team is picked.
		teamSelectionMissing (): boolean {
			return this.scopeFilter === 'team' && !this.teamIdsFilter.length;
		},
		teamSelectionRequiredMessage (): string {
			return `${this.displayLabels.team} selection is required`;
		},
		teamPickerPlaceholder (): string {
			return titleCase(pluralize(this.displayLabels.team));
		},
		teamScopeNeedsSelection (): boolean {
			if (this.scopeFilter !== 'team') return false;
			const eligibleIds = new Set(
				this.teamOptions.filter((option) => !option.disabled).map((option) => option.value),
			);
			return !this.teamIdsFilter.length
				|| this.teamIdsFilter.some((id: string) => !eligibleIds.has(id));
		},
	},
	watch: {
		// Reload the "User email" member list when the selected team set changes.
		relevantTeamIdsKey: {
			immediate: true,
			handler () {
				this.loadMembers();
			},
		},
		// Reload the "Board" list when the scope (personal vs the selected team
		// set) changes.
		boardLoadKey: {
			immediate: true,
			handler () {
				this.loadBoards();
			},
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		// Entering Team seeds the picker only when the currently active Team is
		// eligible. Otherwise it stays empty until the caller makes an explicit
		// eligible choice.
		selectScope (value: LibraryScope) {
			this.scopeFilter = value;
			if (value === 'team' && !this.teamIdsFilter.length) {
				const activeId: string = (this.team && this.team.id) || '';
				const option = this.teamOptions.find((o) => o.value === activeId);
				if (activeId && option && !option.disabled) {
					this.teamIdsFilter = [activeId];
				}
			}
			// Leaving Team drops the selection: a stale set would silently narrow
			// a later Team view to teams the user picked in a different session.
			if (value === 'personal' && this.teamIdsFilter.length) {
				this.teamIdsFilter = [];
			}
		},
		// Every proposal from the Team picker lands here. Locked memberships are
		// stripped and explained, and the surviving ids are assigned back as a new
		// array so the picker re-renders from the reconciled value: dropping the
		// proposal on the floor instead left the picker showing chips the draft
		// never held, and a later edit to that phantom set was refused whole.
		onTeamIdsInput (proposedIds: string[]): void {
			const { teamIds, rejected } = reconcileTeamSelection(proposedIds, this.teamOptions);
			this.teamIdsFilter = teamIds;
			if (rejected.length) this.warnUnavailableTeams();
		},
		// Refuse a locked row before Vuetify's own handler sees the activation.
		// Putting the value back afterwards is not enough on its own: whatever row
		// was clicked gets re-highlighted as the menu cursor (selectItem defers a
		// setMenuIndex, which paints v-list-item--highlighted on the tile), so a
		// refused row kept a chosen-looking overlay until the cursor moved again.
		onLockedTeamActivate (option: FacetOption, event: Event): void {
			if (!option.disabled) return;
			// Vuetify's own click listener is on this same element and is registered
			// after ours, so stopping propagation alone would still let it run when
			// the row itself is the target (Enter on the menu cursor clicks a tile).
			event.stopImmediatePropagation();
			event.preventDefault();
			this.warnUnavailableTeams();
		},
		warnUnavailableTeams (): void {
			this.updateSnackbar({
				status: SNACKBAR_STATUS.WARN,
				message: `You don't have permission to view other users' generations on this ${this.displayLabels.team}.`,
				show: true,
			});
		},
		// Commit the draft to the applied snapshot and notify the host.
		applyFilters () {
			if (this.teamScopeNeedsSelection) return;
			this.$emit('apply', buildAppliedLibraryFilters(this.currentDraft));
			this.$emit('close');
		},
		// Clear every field and commit the empty snapshot (filters live-clear).
		clearAllFilters () {
			const { draft, applied } = clearAndApplyLibraryFilters();
			this.setDraft(draft);
			this.$emit('apply', applied);
		},
		// Clear only the date range from the draft. Apply still commits it, like
		// every other clearable facet, and the other draft fields stay untouched.
		clearDateRange () {
			this.dateFromFilter = '';
			this.dateToFilter = '';
		},
		// Cancel discards the draft and restores the host's latest committed
		// snapshot. Clear All is the explicit path that commits an empty set.
		cancelFilters () {
			this.setDraft(buildDraftFromApplied(this.initialApplied || {}));
			this.$emit('close');
		},
		// Public (via ref): clear everything — used on dialog tab change.
		reset () {
			this.clearAllFilters();
		},
		// Public (via ref): seed draft + commit from an applied snapshot.
		hydrate (applied: AppliedLibraryFilters) {
			this.setDraft(buildDraftFromApplied(applied));
			this.$emit('apply', buildAppliedLibraryFilters(this.currentDraft));
		},
		// Write a draft snapshot onto the control fields. buildDraftFromApplied and
		// createEmptyLibraryFilterDraft both normalize per field kind, so this only
		// has to assign.
		setDraft (draft: Partial<LibraryFilterDraft>) {
			LIBRARY_FILTER_FIELDS.forEach((f) => {
				this[f.draftKey] = normalizeLibraryFilterValue(f, draft[f.draftKey]);
			});
		},
		// Load the members for the selected team(s) into the "User email" picker.
		// Each team is fetched once and cached; the picker unions the selected
		// teams and searches client-side.
		async loadMembers (): Promise<void> {
			const ids: string[] = this.relevantTeamIds;
			if (!ids.length) {
				this.memberOptionsLoaded = [];
				return;
			}
			this.loadingMembers = true;
			try {
				await Promise.all(ids.map((id: string) => this.ensureTeamMembers(id)));
			} finally {
				this.loadingMembers = false;
			}
			const byId: Record<string, FacetOption> = {};
			ids.forEach((id) => {
				(this.memberCacheByTeam[id] || []).forEach((m) => {
					if (!byId[m.value]) byId[m.value] = m;
				});
			});
			this.memberOptionsLoaded = Object.values(byId).sort((a, b) => a.text.localeCompare(b.text));
		},
		// Fetch one team's accepted members (value: userId, text: email), capped.
		async ensureTeamMembers (teamId: string): Promise<void> {
			if (this.memberCacheByTeam[teamId]) return;
			try {
				const snap = await db.collection('teams').doc(teamId).collection('teamMembers')
					.where('isAccepted', '==', true)
					.orderBy('email', 'asc')
					.limit(this.MEMBER_LOAD_CAP)
					.get();
				const members: FacetOption[] = [];
				snap.forEach((doc) => {
					const d = doc.data();
					if (d && d.userId && d.email) members.push({ value: d.userId, text: d.email });
				});
				this.$set(this.memberCacheByTeam, teamId, members);
			} catch (e) {
				// A team whose members the caller can't read stays empty (cached so
				// we don't retry-loop) rather than wedging the picker.
				this.$set(this.memberCacheByTeam, teamId, []);
				console.error('Failed to load team members for the library email filter', e);
			}
		},
		// Load the "Board" picker options for the active scope (cached per key).
		// Mine lists every board you can access (personal + all your teams');
		// Team lists the scanned teams' accessible boards (see
		// libraryBoards.loadBoardsForScope). A stale async resolve is ignored, and
		// a board no longer offered is dropped.
		async loadBoards (): Promise<void> {
			const userId: string = (this.user && this.user.id) || '';
			if (!userId) {
				this.boardOptionsLoaded = [];
				return;
			}
			const includePersonal: boolean = this.scopeFilter === 'personal';
			const teamIds: string[] = this.boardScanTeamIds;
			// Nothing to scan (e.g. Team with no teams picked) and not
			// including personal → empty picker.
			if (!includePersonal && !teamIds.length) {
				this.boardOptionsLoaded = [];
				return;
			}
			const key: string = this.boardLoadKey;
			if (this.boardCacheByKey[key]) {
				this.applyBoardOptions(this.boardCacheByKey[key]);
				return;
			}
			this.loadingBoards = true;
			try {
				const options: FacetOption[] = await loadBoardsForScope({ userId, teamIds, includePersonal });
				this.$set(this.boardCacheByKey, key, options);
				// Ignore if the scope moved on while we were loading.
				if (this.boardLoadKey === key) this.applyBoardOptions(options);
			} finally {
				if (this.boardLoadKey === key) this.loadingBoards = false;
			}
		},
		// Adopt a loaded board list, dropping a selection the new scope no longer
		// offers so the picker never shows a value outside its items.
		applyBoardOptions (options: FacetOption[]): void {
			this.boardOptionsLoaded = options;
			if (this.boardFilter && !options.some((o) => o.value === this.boardFilter)) {
				this.boardFilter = '';
			}
		},
	},
});
